<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <div class="page-content">
      <AppBreadcrumb
        v-if="!selectedTicket"
        :items="[{ label: pageTitle }]"
      >
        <template #actions>
          <a-popconfirm
            :title="$t('tickets.clearConfirmTitle')"
            :ok-text="$t('common.yes')"
            :cancel-text="$t('common.no')"
            @confirm="clearAllTicketResults"
            placement="bottomRight"
          >
            <a-button 
              danger 
              type="primary" 
              :loading="clearing"
              :disabled="!apiTickets.length"
              :size="isMobile ? 'small' : 'middle'"
            >
              <template #icon><DeleteOutlined /></template>
              <span v-if="!isMobile">{{ $t('common.clear') }}</span>
            </a-button>
          </a-popconfirm>
        </template>
      </AppBreadcrumb>

      <template v-if="!selectedTicket">
        <a-alert
          v-if="errorMessage"
          type="error"
          show-icon
          :message="errorMessage"
          style="margin-bottom: 16px"
          class="mobile-alert"
        />

        <TicketList
          :tickets="normalizedTickets"
          :loading="loading"
          @select="startTicket"
        />
      </template>

      <template v-else>
        <TicketTestRoom
          :title="selectedTicket.title"
          :questions="selectedTicket.questions"
          :resultId="selectedTicket.resultId"
          :backRoute="`/tickets/${type}`"
          :pageTitle="pageTitle"
          :ticketType="Number(type)"
          :ticketNumber="selectedTicket.ticketNumber"
          @back="goBackToTickets"
          @go-list="goBackToTickets"
          @finished="clearTicketSession"
        />
      </template>
    </div>
  </div>
</template>

<script>
import api, { MEDIA_BASE_URL } from '@/api/axios'
import { message } from 'ant-design-vue'
import { DeleteOutlined } from '@ant-design/icons-vue'
import HeaderComponent from '../components/HeaderComponent.vue'
import AppBreadcrumb from '../components/AppBreadcrumb.vue'
import TicketList from '../components/TicketList.vue'
import TicketTestRoom from '../components/TicketTestRoom.vue'

export default {
  name: 'TicketCategoryView',

  components: {
    HeaderComponent,
    AppBreadcrumb,
    TicketList,
    TicketTestRoom,
    DeleteOutlined
  },

  data() {
    return {
      selectedTicket: null,
      loading: false,
      clearing: false,
      errorMessage: '',
      apiTickets: [],
      isMobile: window.innerWidth <= 768
    }
  },

  computed: {
    type() {
      return this.$route.params.type
    },

    ticketNumberFromRoute() {
      const n = Number(this.$route.params.ticketNumber)
      return Number.isFinite(n) && n > 0 ? n : null
    },

    normalizedTickets() {
      return (this.apiTickets || []).map((ticket, index) => ({
        ...ticket,
        id: ticket.ticket_number || index + 1,
        number: ticket.ticket_number || index + 1,
        ticketNumber: ticket.ticket_number || index + 1,
        title: this.getTicketTitle(ticket.ticket_number || index + 1),
        stats: ticket.stats ? {
          ...ticket.stats,
          scoreText: this.getScoreText(ticket.stats.score_percent)
        } : null
      }))
    },

    pageTitle() {
      const types = {
        '10': this.$t('tickets.ticket10Title'),
        '20': this.$t('tickets.ticket20Title'),
        '50': this.$t('tickets.ticket50Title')
      }
      return types[this.type] || this.$t('tickets.ticketsTitle')
    }
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },

  watch: {
    '$route.params.type': {
      immediate: true,
      async handler() {
        this.selectedTicket = null
        await this.fetchTicketNumbers()
      }
    },

    '$route.params.ticketNumber': {
      immediate: false,
      async handler(newVal, oldVal) {
        if (newVal === oldVal) return
        if (!this.apiTickets.length) return

        const routeTicketNumber = Number(newVal)

        if (!routeTicketNumber) {
          this.selectedTicket = null
          return
        }

        if (this.selectedTicket?.ticketNumber === routeTicketNumber) return

        await this.restoreOrStartTicketByRoute(routeTicketNumber)
      }
    }
  },

  methods: {
    getTicketTitle(ticketNumber) {
      return this.$t('tickets.ticketLabel', { number: ticketNumber })
    },

    getScoreText(scorePercent) {
      const score = scorePercent || 0
      if (score >= 80) return this.$t('tickets.excellent')
      if (score >= 60) return this.$t('tickets.good')
      if (score >= 40) return this.$t('tickets.satisfactory')
      return this.$t('tickets.poor')
    },

    getSessionStorageKey(ticketNumber = this.ticketNumberFromRoute) {
      return `ticket_session_${this.type}_${ticketNumber}`
    },

    saveSelectedTicketMeta(ticket) {
      if (!ticket?.ticketNumber) return
      localStorage.setItem(
        this.getSessionStorageKey(ticket.ticketNumber),
        JSON.stringify({
          selectedTicket: {
            title: ticket.title,
            ticketNumber: ticket.ticketNumber,
            resultId: ticket.resultId,
            questions: ticket.questions || []
          }
        })
      )
    },

    readTicketSession(ticketNumber = this.ticketNumberFromRoute) {
      try {
        const raw = localStorage.getItem(this.getSessionStorageKey(ticketNumber))
        return raw ? JSON.parse(raw) : null
      } catch {
        return null
      }
    },

    clearTicketSession(ticketNumber = this.ticketNumberFromRoute) {
      if (!ticketNumber) return
      localStorage.removeItem(this.getSessionStorageKey(ticketNumber))
    },

    async fetchTicketNumbers() {
      this.loading = true
      this.errorMessage = ''
      this.apiTickets = []

      try {
        const ticketType = Number(this.type)

        if (![10, 20, 50].includes(ticketType)) {
          throw new Error(this.$t('tickets.invalidTicketType'))
        }

        const response = await api.post(
          `/v1/tests/ticket-numbers/`,
          { ticket_type: ticketType }
        )

        const payload = response.data || {}
        this.apiTickets = payload.tickets || []

        if (!this.apiTickets.length) {
          this.errorMessage = payload.detail || this.$t('tickets.noTicketsFound')
          return
        }

        if (this.ticketNumberFromRoute) {
          await this.restoreOrStartTicketByRoute(this.ticketNumberFromRoute)
        }
      } catch (error) {
        console.error('fetchTicketNumbers xatosi:', error)
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.message ||
          this.$t('tickets.fetchError')
      } finally {
        this.loading = false
      }
    },

    async restoreOrStartTicketByRoute(ticketNumber) {
      const saved = this.readTicketSession(ticketNumber)

      if (saved?.selectedTicket?.ticketNumber === ticketNumber) {
        this.selectedTicket = saved.selectedTicket
        return
      }

      const ticket = this.normalizedTickets.find(
        (item) => Number(item.ticketNumber) === Number(ticketNumber)
      )

      if (ticket) {
        await this.startTicket(ticket, { pushRoute: false })
      } else {
        this.errorMessage = this.$t('tickets.ticketNotFound', { number: ticketNumber })
        this.$router.replace(`/tickets/${this.type}`)
      }
    },

    async clearAllTicketResults() {
      this.clearing = true
      
      try {
        const response = await api.delete(
          `/v1/tests/clear-ticket-results/?ticket_type=${this.type}`
        )

        if (response.data) {
          message.success(response.data.message || this.$t('tickets.clearSuccess'))
          await this.fetchTicketNumbers()
        }
      } catch (error) {
        console.error('Clear results error:', error)
        message.error(error.response?.data?.detail || this.$t('tickets.clearError'))
      } finally {
        this.clearing = false
      }
    },

    async startTicket(ticket, options = {}) {
      if (this.loading) return

      const { pushRoute = true } = options

      this.loading = true
      this.errorMessage = ''

      try {
        const ticketNumber =
          ticket.ticketNumber ||
          ticket.ticket_number ||
          ticket.number ||
          ticket.id

        const response = await api.post(
          `/v1/tests/start-ticket/`,
          {
            ticket_type: Number(this.type),
            ticket_number: Number(ticketNumber)
          }
        )

        const data = response.data || {}
        const result = data.result || {}
        const questions = data.questions || []
        this.selectedTicket = {
          title: this.getTicketTitle(ticketNumber),
          ticketNumber: Number(ticketNumber),
          resultId: result.id,
          questions
        }

        this.saveSelectedTicketMeta(this.selectedTicket)

        if (pushRoute) {
          this.$router.push(`/tickets/${this.type}/${ticketNumber}`)
        }
      } catch (error) {
        console.error('startTicket xatosi:', error)
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.message ||
          this.$t('tickets.startError')
      } finally {
        this.loading = false
      }
    },

    goBackToTickets() {
      if (this.selectedTicket?.ticketNumber) {
        this.clearTicketSession(this.selectedTicket.ticketNumber)
      }

      this.selectedTicket = null
      this.errorMessage = ''

      this.$router.push(`/tickets/${this.type}`)
    }
  }
}


</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg);
}

.page-content {
  flex: 1;
  padding: 0 24px 32px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

/* Mobile (max-width: 768px) */
@media (max-width: 768px) {
  .page-content {
    padding: 0 16px 24px;
  }
  
  .mobile-alert {
    margin-bottom: 12px !important;
  }
  
  .mobile-alert :deep(.ant-alert-message) {
    font-size: 13px;
  }
  
  :deep(.ant-btn) {
    font-size: 12px;
  }
  
  :deep(.ant-btn-icon) {
    font-size: 14px;
  }
}

/* Small Mobile (max-width: 480px) */
@media (max-width: 480px) {
  .page-content {
    padding: 0 12px 20px;
  }
}

/* Tablet (768px - 1024px) */
@media (min-width: 769px) and (max-width: 1024px) {
  .page-content {
    padding: 0 20px 28px;
  }
}
</style>

<style>
/* Global message z-index */
.ant-message {
  z-index: 99999 !important;
}

.ant-popconfirm-buttons span {
  color: #010101;
}

/* Mobile popconfirm */
@media (max-width: 768px) {
  .ant-popover-inner {
    max-width: 280px;
  }
  
  .ant-popconfirm-buttons button {
    font-size: 12px;
    padding: 4px 12px;
  }
}
</style>